import { useCallback, useMemo, useRef, useState } from 'react';
import { NumberInput } from '@mantine/core';

import { NumberInputRightSection } from './number-input-right-section';

import type { NumberInputHandlers, NumberInputProps } from '@mantine/core';

type CustomNumberInputProps = Omit<NumberInputProps, 'handlersRef' | 'hideControls' | 'rightSection' | 'rightSectionPointerEvents'> & {
  internalLabel?: string;
  /**
   * When true, focusing an untouched field whose value still equals `defaultValue` blanks the
   * displayed value so the user can type over it instead of deleting the default first.
   */
  clearsDefaultOnFocus?: boolean;
};

export const CustomNumberInput = ({
  internalLabel,
  disabled,
  rightSectionWidth,
  clearsDefaultOnFocus = false,
  value,
  defaultValue,
  onFocus,
  onBlur,
  onChange,
  ...rest
}: CustomNumberInputProps) => {
  const handlersRef = useRef<NumberInputHandlers | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);
  const [hasFocus, setHasFocus] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);

  const displayValue = useMemo(() => {
    if (clearsDefaultOnFocus && hasFocus && !hasChanged && value === defaultValue) {
      return '';
    }
    return value;
  }, [clearsDefaultOnFocus, hasFocus, hasChanged, value, defaultValue]);

  const handleFocus: React.FocusEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      setHasFocus(true);
      onFocus?.(event);
    },
    [onFocus],
  );

  const handleBlur: React.FocusEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      setHasFocus(false);
      onBlur?.(event);
    },
    [onBlur],
  );

  const handleChange = useCallback(
    (newValue: string | number) => {
      setHasChanged(true);
      onChange?.(newValue);
    },
    [onChange],
  );

  return (
    <NumberInput
      ref={inputRef}
      defaultValue={defaultValue}
      disabled={disabled}
      handlersRef={handlersRef}
      hideControls
      onBlur={handleBlur}
      onChange={handleChange}
      onFocus={handleFocus}
      rightSection={<NumberInputRightSection disabled={disabled} handlersRef={handlersRef} inputRef={inputRef} label={internalLabel} />}
      rightSectionPointerEvents={disabled ? 'none' : 'all'}
      rightSectionWidth={rightSectionWidth ?? (internalLabel ? 70 : 40)}
      value={displayValue}
      {...rest}
    />
  );
};
